import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Download, ExternalLink, Filter } from "lucide-react";

const STATUS_STYLES = {
    NOT_STARTED: "bg-neutral-100 text-neutral-700 border-neutral-200",
    IN_PROGRESS: "bg-blue-50 text-blue-800 border-blue-200",
    COMPLETED: "bg-emerald-50 text-emerald-800 border-emerald-200",
    OVERDUE: "bg-red-50 text-red-800 border-red-200",
};

function StatusChip({ status }) {
    return (
        <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[status] || STATUS_STYLES.NOT_STARTED}`}>
            {status.replace("_", " ")}
        </span>
    );
}

async function downloadCertificatePdf(cert) {
    try {
        const url = cert.pdf_download_url || `/api/certificates/${cert.id}/pdf`;
        const r = await api.get(url.replace(/^\/api/, ""), { responseType: "blob" });
        const href = URL.createObjectURL(r.data);
        const link = document.createElement("a");
        link.href = href;
        link.download = `certificate_${cert.id.slice(0, 8)}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(href);
    } catch (e) {
        toast.error(formatApiError(e));
    }
}

// ------------------ Assignments ------------------
export function AdminAssignments() {
    const [rows, setRows] = useState(null);
    const [modules, setModules] = useState([]);
    const [users, setUsers] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [moduleFilter, setModuleFilter] = useState("");
    const [selectedModule, setSelectedModule] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [dueAt, setDueAt] = useState("");
    const [creating, setCreating] = useState(false);

    async function load() {
        try {
            const [a, m, u] = await Promise.all([
                api.get("/assignments"), api.get("/modules"), api.get("/users"),
            ]);
            setRows(a.data);
            setModules((m.data.items || m.data || []).filter((x) => x.status === "PUBLISHED"));
            setUsers((u.data.items || u.data || []).filter((x) => x.role === "LEARNER" && x.is_active));
        } catch (e) {
            toast.error(formatApiError(e));
            setRows([]);
        }
    }
    useEffect(() => { load(); }, []);

    async function assign() {
        if (!selectedModule || selectedUsers.length === 0) return;
        setCreating(true);
        try {
            const r = await api.post("/assignments", {
                module_id: selectedModule, target_user_ids: selectedUsers,
                due_at: dueAt ? new Date(dueAt).toISOString() : null,
            });
            toast.success(`Created ${r.data.count} assignment(s)`);
            setSelectedUsers([]); setDueAt("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCreating(false); }
    }

    if (!rows) return <div className="text-neutral-500">Loading…</div>;

    const filtered = rows.filter((r) => (
        (!statusFilter || r.status === statusFilter)
        && (!moduleFilter || r.module_id === moduleFilter)
    ));
    const userNameById = Object.fromEntries(users.map((u) => [u.id, u.name]));

    return (
        <div data-testid="page-admin-assignments">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Assignments</h1>
                <p className="text-sm text-neutral-500 mt-0.5">Assign a published module to learners. Track progress and scores.</p>
            </div>

            <Card className="mb-6">
                <CardContent className="p-5 space-y-3">
                    <div className="text-sm font-semibold text-neutral-900">Create assignment</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <select
                            value={selectedModule}
                            onChange={(e) => setSelectedModule(e.target.value)}
                            className="rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white"
                            data-testid="assign-module-select"
                        >
                            <option value="">— pick a published module —</option>
                            {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                        <select
                            multiple
                            value={selectedUsers}
                            onChange={(e) => setSelectedUsers(Array.from(e.target.selectedOptions).map((o) => o.value))}
                            className="rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white min-h-24"
                            data-testid="assign-user-select"
                        >
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
                        </select>
                        <Input
                            type="datetime-local"
                            value={dueAt}
                            onChange={(e) => setDueAt(e.target.value)}
                            data-testid="assign-due-at"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={assign}
                            disabled={!selectedModule || selectedUsers.length === 0 || creating}
                            data-testid="btn-assign"
                        >
                            {creating ? "Creating…" : `Assign to ${selectedUsers.length || 0} learner${selectedUsers.length === 1 ? "" : "s"}`}
                        </Button>
                        <span className="text-xs text-neutral-400">Ctrl/⌘-click to multi-select learners.</span>
                    </div>
                </CardContent>
            </Card>

            <div className="mb-3 flex items-center gap-3">
                <Filter size={14} className="text-neutral-400" />
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs bg-white"
                    data-testid="filter-status"
                >
                    <option value="">All statuses</option>
                    <option value="NOT_STARTED">Not started</option>
                    <option value="IN_PROGRESS">In progress</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="OVERDUE">Overdue</option>
                </select>
                <select
                    value={moduleFilter}
                    onChange={(e) => setModuleFilter(e.target.value)}
                    className="rounded-md border border-neutral-200 px-3 py-1.5 text-xs bg-white"
                    data-testid="filter-module"
                >
                    <option value="">All modules</option>
                    {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                <span className="ml-auto text-xs text-neutral-400">{filtered.length} of {rows.length}</span>
            </div>

            <Card>
                <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left p-3">Module</th>
                                <th className="text-left p-3">Assignee</th>
                                <th className="text-left p-3">Status</th>
                                <th className="text-left p-3">Due</th>
                                <th className="text-right p-3">Watch</th>
                                <th className="text-right p-3">Best score</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                            {filtered.map((r) => (
                                <tr key={r.id} data-testid={`admin-assignment-${r.id}`}>
                                    <td className="p-3 font-medium text-neutral-900">{r.module_name || r.module_id.slice(0, 8)}</td>
                                    <td className="p-3 text-neutral-700">
                                        {userNameById[r.assignee_user_id] || r.assignee_user_id.slice(0, 8)}
                                    </td>
                                    <td className="p-3"><StatusChip status={r.status} /></td>
                                    <td className="p-3 text-neutral-500 text-xs">
                                        {r.due_at ? new Date(r.due_at).toLocaleDateString() : "—"}
                                    </td>
                                    <td className="p-3 text-right text-neutral-700">
                                        {r.progress?.watch_percentage != null ? `${r.progress.watch_percentage.toFixed(0)}%` : "—"}
                                    </td>
                                    <td className="p-3 text-right text-neutral-700">
                                        {r.progress?.best_score != null ? `${r.progress.best_score.toFixed(0)}%` : "—"}
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={6} className="p-8 text-center text-neutral-500" data-testid="empty-assignments">No assignments match the filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}

// ------------------ Learners ------------------
export function AdminLearners() {
    const [rows, setRows] = useState(null);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);

    async function load() {
        try {
            const r = await api.get("/learners");
            setRows(r.data);
        } catch (e) { toast.error(formatApiError(e)); setRows([]); }
    }
    useEffect(() => { load(); }, []);

    async function openLearner(u) {
        setSelected(u); setDetail(null);
        try {
            const [assignments, attempts] = await Promise.all([
                api.get(`/assignments`, { params: { } }),
                api.get(`/quiz-attempts`, { params: { learner_id: u.id } }),
            ]);
            setDetail({
                assignments: (assignments.data || []).filter((a) => a.assignee_user_id === u.id),
                attempts: attempts.data || [],
            });
        } catch (e) { toast.error(formatApiError(e)); }
    }

    if (!rows) return <div className="text-neutral-500">Loading…</div>;
    return (
        <div data-testid="page-admin-learners">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Learners</h1>
                <p className="text-sm text-neutral-500 mt-0.5">Every learner in your organisation with completion rate and last activity.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className={selected ? "lg:col-span-2" : "lg:col-span-3"}>
                    <Card>
                        <CardContent className="p-0 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                                    <tr>
                                        <th className="text-left p-3">Name</th>
                                        <th className="text-left p-3">Department</th>
                                        <th className="text-right p-3">Assigned</th>
                                        <th className="text-right p-3">Completed</th>
                                        <th className="text-right p-3">Completion</th>
                                        <th className="text-right p-3">Avg score</th>
                                        <th className="text-right p-3">Last activity</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-neutral-100">
                                    {rows.map((u) => {
                                        const rate = u.assignments_total > 0
                                            ? Math.round(100 * u.assignments_completed / u.assignments_total)
                                            : null;
                                        return (
                                            <tr
                                                key={u.id}
                                                onClick={() => openLearner(u)}
                                                data-testid={`learner-row-${u.id}`}
                                                className={`cursor-pointer hover:bg-neutral-50 ${selected?.id === u.id ? "bg-neutral-50" : ""}`}
                                            >
                                                <td className="p-3">
                                                    <div className="font-medium text-neutral-900">{u.name}</div>
                                                    <div className="text-xs text-neutral-500">{u.email}</div>
                                                </td>
                                                <td className="p-3 text-neutral-700">{u.department || "—"}</td>
                                                <td className="p-3 text-right">{u.assignments_total}</td>
                                                <td className="p-3 text-right">{u.assignments_completed}</td>
                                                <td className="p-3 text-right">{rate != null ? `${rate}%` : "—"}</td>
                                                <td className="p-3 text-right">{u.average_score != null ? `${u.average_score}%` : "—"}</td>
                                                <td className="p-3 text-right text-xs text-neutral-500">
                                                    {u.last_activity_at ? new Date(u.last_activity_at).toLocaleDateString() : "—"}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {rows.length === 0 && (
                                        <tr><td colSpan={7} className="p-10 text-center text-neutral-500" data-testid="empty-learners">No learners yet.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>
                </div>
                {selected && (
                    <div>
                        <Card data-testid="learner-drawer">
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="font-semibold text-neutral-900">{selected.name}</div>
                                        <div className="text-xs text-neutral-500">{selected.email}</div>
                                    </div>
                                    <button onClick={() => { setSelected(null); setDetail(null); }} className="text-neutral-400 hover:text-neutral-700" data-testid="btn-close-drawer">×</button>
                                </div>
                                {!detail ? (
                                    <div className="text-sm text-neutral-500">Loading timeline…</div>
                                ) : (
                                    <>
                                        <div className="text-[11px] uppercase tracking-wider text-neutral-400 mb-2">Assignments</div>
                                        <div className="space-y-1.5 mb-4">
                                            {detail.assignments.length === 0 && <div className="text-xs text-neutral-500">None</div>}
                                            {detail.assignments.map((a) => (
                                                <div key={a.id} className="flex items-center gap-2 text-xs">
                                                    <StatusChip status={a.status} />
                                                    <span className="flex-1 truncate">{a.module_name}</span>
                                                    {a.progress?.best_score != null && <span className="text-neutral-500">{a.progress.best_score.toFixed(0)}%</span>}
                                                </div>
                                            ))}
                                        </div>
                                        <div className="text-[11px] uppercase tracking-wider text-neutral-400 mb-2">Recent attempts</div>
                                        <div className="space-y-1.5">
                                            {detail.attempts.length === 0 && <div className="text-xs text-neutral-500">No attempts yet.</div>}
                                            {detail.attempts.slice(0, 8).map((at) => (
                                                <div key={at.id} className="flex items-center gap-2 text-xs">
                                                    <Badge className={at.passed ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-800 border-red-200"}>
                                                        {at.passed ? "PASS" : "FAIL"}
                                                    </Badge>
                                                    <span className="flex-1">Score {at.score.toFixed(0)}%</span>
                                                    <span className="text-neutral-500">{at.completed_at ? new Date(at.completed_at).toLocaleDateString() : "—"}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
}

// ------------------ Assessments ------------------
export function AdminAssessments() {
    const [modules, setModules] = useState([]);
    const [selected, setSelected] = useState("");
    const [quiz, setQuiz] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const m = await api.get("/modules");
            setModules((m.data.items || m.data || []).filter((x) => x.current_version_id));
        } catch (e) { toast.error(formatApiError(e)); }
    }
    useEffect(() => { load(); }, []);

    async function loadQuiz(mid) {
        setSelected(mid); setQuiz(null); setAnalytics(null);
        if (!mid) return;
        try {
            const q = await api.get(`/modules/${mid}/quiz`);
            setQuiz(q.data);
            const a = await api.get(`/quizzes/${q.data.id}/analytics`);
            setAnalytics(a.data);
        } catch (e) { setQuiz({ error: formatApiError(e) }); }
    }
    async function generate() {
        setBusy(true);
        try {
            await api.post(`/modules/${selected}/quiz/generate`);
            await loadQuiz(selected);
            toast.success("Quiz generated");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    async function approve() {
        if (!quiz?.id) return;
        try {
            await api.post(`/quizzes/${quiz.id}/approve`, { approve: !quiz.approved });
            await loadQuiz(selected);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <div data-testid="page-admin-assessments">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Assessments</h1>
                <p className="text-sm text-neutral-500 mt-0.5">Pass rate, average score, and per-question miss rate.</p>
            </div>
            <div className="mb-6">
                <select
                    value={selected}
                    onChange={(e) => loadQuiz(e.target.value)}
                    className="rounded-md border border-neutral-200 px-3 py-2 text-sm bg-white min-w-[320px]"
                    data-testid="assess-module-select"
                >
                    <option value="">— pick a module —</option>
                    {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
            </div>
            {selected && !quiz && <div className="text-neutral-500">Loading…</div>}
            {quiz?.error && (
                <Card><CardContent className="p-6 text-center">
                    <div className="text-neutral-500 mb-4" data-testid="quiz-error">{quiz.error}</div>
                    <Button onClick={generate} disabled={busy} data-testid="btn-generate-quiz">
                        {busy ? "Generating…" : "Generate quiz with AI"}
                    </Button>
                </CardContent></Card>
            )}
            {quiz?.id && (
                <>
                    <Card className="mb-4">
                        <CardContent className="p-5 grid grid-cols-2 md:grid-cols-5 gap-6 items-center">
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-neutral-500">Questions</div>
                                <div className="text-2xl font-semibold text-neutral-900">{quiz.total_questions}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-neutral-500">Pass mark</div>
                                <div className="text-2xl font-semibold text-neutral-900">{quiz.passing_score}%</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-neutral-500">Attempts</div>
                                <div className="text-2xl font-semibold text-neutral-900">{analytics?.attempts ?? 0}</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-wider text-neutral-500">Pass rate</div>
                                <div className="text-2xl font-semibold text-neutral-900">{analytics?.pass_rate != null ? `${analytics.pass_rate}%` : "—"}</div>
                            </div>
                            <div className="flex flex-col gap-1.5 items-end">
                                <Button size="sm" onClick={approve} data-testid="btn-approve-quiz" variant={quiz.approved ? "outline" : "default"}>
                                    {quiz.approved ? "Revoke approval" : "Approve for learners"}
                                </Button>
                                <Button size="sm" variant="outline" onClick={generate} disabled={busy} data-testid="btn-regen-quiz">Regenerate</Button>
                            </div>
                        </CardContent>
                    </Card>
                    <div className="space-y-3">
                        {(quiz.questions || []).map((q, i) => {
                            const missRate = analytics?.questions?.find((x) => x.question_id === q.id)?.miss_rate;
                            return (
                                <Card key={q.id} data-testid={`admin-question-${i}`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-2 text-xs text-neutral-500 mb-2">
                                            <span className="font-mono text-neutral-400">{String(i + 1).padStart(2, "0")}</span>
                                            <Badge variant="outline">{q.kind}</Badge>
                                            <Badge variant="outline">{q.difficulty}</Badge>
                                            {missRate != null && (
                                                <span className={`ml-auto ${missRate > 50 ? "text-red-600" : "text-neutral-500"}`}>
                                                    Miss rate {missRate}%
                                                </span>
                                            )}
                                        </div>
                                        <div className="font-medium text-neutral-900 mb-2">{q.prompt}</div>
                                        <ul className="text-sm space-y-1">
                                            {(q.options || []).map((o) => (
                                                <li key={o.id} className={o.is_correct ? "text-emerald-700" : "text-neutral-600"}>
                                                    {o.is_correct ? "✓ " : "· "}{o.text}
                                                </li>
                                            ))}
                                        </ul>
                                        {q.explanation && <div className="mt-2 text-xs text-neutral-500">Why: {q.explanation}</div>}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}

// ------------------ Certificates ------------------
export function AdminCertificates() {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/certificates").then((r) => setRows(r.data)).catch((e) => {
            toast.error(formatApiError(e)); setRows([]);
        });
    }, []);
    if (!rows) return <div className="text-neutral-500">Loading…</div>;
    return (
        <div data-testid="page-admin-certificates">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Certificates</h1>
                <p className="text-sm text-neutral-500 mt-0.5">Every certificate issued in your organisation.</p>
            </div>
            <Card>
                <CardContent className="p-0 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left p-3">Learner</th>
                                <th className="text-left p-3">Module</th>
                                <th className="text-left p-3">Cert ID</th>
                                <th className="text-right p-3">Score</th>
                                <th className="text-right p-3">Completed</th>
                                <th className="text-right p-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-neutral-100">
                            {rows.map((c) => (
                                <tr key={c.id} data-testid={`cert-row-${c.id}`}>
                                    <td className="p-3">
                                        <div className="font-medium text-neutral-900">{c.learner_name}</div>
                                        <div className="text-xs text-neutral-500">{c.learner_email}</div>
                                    </td>
                                    <td className="p-3 text-neutral-700">{c.module_title}</td>
                                    <td className="p-3 mono text-xs text-neutral-500">{c.id.slice(0, 8)}</td>
                                    <td className="p-3 text-right">{c.score.toFixed(0)}%</td>
                                    <td className="p-3 text-right text-xs text-neutral-500">
                                        {new Date(c.completion_date).toLocaleDateString()}
                                    </td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex gap-1.5">
                                            {c.pdf_download_url && (
                                                <Button size="sm" variant="outline" onClick={() => downloadCertificatePdf(c)} data-testid={`btn-cert-pdf-${c.id}`}>
                                                    <Download size={12} className="mr-1" />PDF
                                                </Button>
                                            )}
                                            <a href={`/api/certificates/verify/${c.verify_token}`} target="_blank" rel="noreferrer">
                                                <Button size="sm" variant="outline" data-testid={`btn-cert-verify-${c.id}`}>
                                                    <ExternalLink size={12} className="mr-1" />Verify
                                                </Button>
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={6} className="p-10 text-center text-neutral-500" data-testid="empty-admin-certificates">
                                    No certificates issued yet.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}
